import {
  ExperiencePerformanceTypes,
  ExperienceTypes,
  UFOExperience,
} from '@atlassian/ufo';
import { type CustomData } from '@atlassian/ufo-experimental';

import { type MetricNameValue } from './metrics';

const onError = console.error;

export type Include<T, IncludedTypes> = Exclude<T, Exclude<T, IncludedTypes>>;

export type CanvasMetadata = Include<CustomData, Record<any, any>>;

/**
 * Have to recreate this type since it's not exported from the package
 */
export type AbstractExperienceConfig = ConstructorParameters<typeof UFOExperience>[1];

export type MetadataTransformer = (
  metadata: CanvasMetadata | undefined,
) => CanvasMetadata;

type ReliabilityStartOptions = Partial<
  Pick<AbstractExperienceConfig, 'performanceType' | 'type'>
>;

/**
 * Represents a timing measurement for performance tracking in the application.
 *
 * This type is used to define custom timing marks for components, allowing
 * for precise measurement of performance between specified start and end points.
 *
 * For more details on how to instrument frontend performance SLOs, refer to
 * the documentation here:
 * [Instrumenting experience timings](https://developer.atlassian.com/platform/ufo/ufo-core/developer-guide/instrumenting-experiences/#timings).
 */
export type Timing = {
  key: string;
  startMark: string;
  endMark: string;
  component?: string;
};

/**
 * DO NOT use this API to emit metrics every frame. There will be a performance impact.
 *
 * For instrumenting reliability metrics in the frontend of canvas.
 * This abstraction is for emitting good/bad metrics for building TOME SLI/SLOs
 * and other monitoring.
 *
 * By using the success/failure direct methods this API will not measure performance.
 * By using 'start' you can measure performance of a specific action.
 */
export class ReliabilityMetrics {
  private static instance = new ReliabilityMetrics();

  private readonly metadataTransformers: MetadataTransformer[] = [];

  protected constructor() {}

  public static getInstance() {
    return ReliabilityMetrics.instance;
  }

  private experiences: Map<MetricNameValue, UFOExperience> = new Map();

  /**
   * Retrieves an existing `UFOExperience` instance for the given metric name or creates a new one if it doesn't exist.
   */
  private getOrDefault(
    metricName: MetricNameValue,
    histogramBuckets?: number[],
    timings?: Timing[],
    {
      type = ExperienceTypes.Operation,
      performanceType = ExperiencePerformanceTypes.Custom,
    }: ReliabilityStartOptions = {},
  ) {
    let experience = this.experiences.get(metricName);

    if (!experience) {
      experience = new UFOExperience(metricName, {
        type,
        performanceType,
        timings,
        ...(histogramBuckets && histogramBuckets.length > 0
          ? {
              performanceConfig: {
                histogram: {
                  custom: {
                    duration: histogramBuckets.join('_'),
                  },
                },
              },
            }
          : {}),
      });
      this.experiences.set(metricName, experience);
    }

    return experience;
  }

  /**
   * Return the instance of the experience, and begin timing.
   */
  public start(
    metricName: MetricNameValue,
    timings?: Timing[],
    options?: ReliabilityStartOptions,
  ) {
    const experience = this.getOrDefault(metricName, undefined, timings, options);

    this.enhanceWithCustomMetadata(experience, undefined);

    experience.start().catch(onError);
    return experience;
  }

  private withCustomMetadata(metadata: CanvasMetadata | undefined) {
    return this.metadataTransformers.reduce(
      (prevMetadata, transformer) => transformer(prevMetadata),
      metadata,
    );
  }

  /**
   * Enhance the UFO events with custom data. This data will show up in the
   * event under `event.attributes.properties` as a key-value pair with the
   * following structure `custom:<field> : <value>`.
   * For example: `custom:isAnonymous: true`.
   */
  private enhanceWithCustomMetadata(
    experience: UFOExperience,
    metadata: CanvasMetadata | undefined,
  ) {
    const finalMetadata = this.withCustomMetadata(metadata);

    if (finalMetadata !== undefined) {
      experience.addMetadata(finalMetadata);
    }
  }

  addCustomMetadataTransformer(transformer: MetadataTransformer) {
    this.metadataTransformers.push(transformer);
  }

  /**
   * Send a success signal with no performance information
   */
  public success(metricName: MetricNameValue, metadata?: CanvasMetadata) {
    const experience = this.getOrDefault(metricName);

    return experience
      .start()
      .then(() =>
        experience.success({ force: true, metadata: this.withCustomMetadata(metadata) }),
      )
      .catch(onError);
  }

  /**
   * Send a success signal with no static duration
   */
  public successWithDuration(
    metricName: MetricNameValue,
    startedAt: number,
    endedAt: number,
    metadata?: CanvasMetadata,
  ) {
    const experience = this.getOrDefault(metricName);

    return experience
      .start(startedAt)
      .then(() => {
        // Calling .start() will reset the metadata, so we need to apply it after
        this.enhanceWithCustomMetadata(experience, metadata);

        experience.transition(
          {
            id: 'SUCCEEDED',
            final: true,
            success: true,
          },
          endedAt,
        );
      })
      .catch(onError);
  }

  /**
   * Send a failure signal with no performance information
   */
  public failure(
    metricName: MetricNameValue,
    metadata: { errorMessage: string } & CanvasMetadata,
  ) {
    const experience = this.getOrDefault(metricName);

    return experience
      .start()
      .then(() =>
        experience.failure({ force: true, metadata: this.withCustomMetadata(metadata) }),
      )
      .catch(onError);
  }

  /**
   * Send a failure signal with static duration
   */
  public failureWithDuration(
    metricName: MetricNameValue,
    startedAt: number,
    endedAt: number,
    metadata: { errorMessage: string } & CanvasMetadata,
  ) {
    const experience = this.getOrDefault(metricName);

    return experience
      .start(startedAt)
      .then(() => {
        // Calling .start() will reset the metadata, so we need to apply it after
        this.enhanceWithCustomMetadata(experience, metadata);

        experience.transition(
          {
            id: 'FAILED',
            final: true,
            success: false,
          },
          endedAt,
        );
      })
      .catch(onError);
  }

  /**
   * Send an abort signal with no performance information
   */
  public abort(metricName: MetricNameValue, metadata?: Record<string, string | number>) {
    const experience = this.getOrDefault(metricName);

    return experience
      .start()
      .then(() =>
        experience.abort({ force: true, metadata: this.withCustomMetadata(metadata) }),
      )
      .catch(onError);
  }

  /**
   * Send an abort signal with static duration
   */
  public abortWithDuration(
    metricName: MetricNameValue,
    startedAt: number,
    endedAt: number,
    metadata?: Record<string, string | number>,
  ) {
    const experience = this.getOrDefault(metricName);

    return experience
      .start(startedAt)
      .then(() => {
        // Calling .start() will reset the metadata, so we need to apply it after
        this.enhanceWithCustomMetadata(experience, metadata);

        experience.transition(
          {
            id: 'ABORTED',
            final: true,
            success: false,
          },
          endedAt,
        );
      })
      .catch(onError);
  }

  /**
   * Send a success signal which supports histogram reporting
   */
  public histogram(
    metricName: MetricNameValue,
    value: number,
    buckets: number[],
    metadata?: CanvasMetadata,
  ) {
    const experience = this.getOrDefault(metricName, buckets);

    return experience
      .start(0)
      .then(() => {
        // Calling .start() will reset the metadata, so we need to apply it after
        this.enhanceWithCustomMetadata(experience, metadata);

        experience.transition(
          {
            id: 'SUCCEEDED',
            final: true,
            success: true,
          },
          value,
        );
      })
      .catch(onError);
  }
}
